import axios from 'axios';
import cacheService from './cacheService';
import networkUtils from '../utils/networkUtils';

// API base configuration
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// API Key - you can store this in environment variables for production
const API_KEY = process.env.REACT_APP_API_KEY;

// Create axios instance
const api = axios.create({
	baseURL: API_BASE_URL,
	headers: {
		'Content-Type': 'application/json',
		'Authorization': `Bearer ${API_KEY}`,
		'api-key': API_KEY,
	},
	// withCredentials: true, // Temporarily disabled due to CORS issues
});

// Request interceptor to add auth token
api.interceptors.request.use(
	(config) => {
		const token = localStorage.getItem('authToken');
		if (token) {
			config.headers.Authorization = `Bearer ${token}`;
		}
		return config;
	},
	(error) => {
		return Promise.reject(error);
	}
);

// Response interceptor for error handling
api.interceptors.response.use(
	(response) => response,
	(error) => {
		// Only redirect on 401 if we're not on login/register pages and have a token
		if (error.response?.status === 401) {
			const currentPath = window.location.pathname;
			const isAuthPage = currentPath.startsWith('/login') || currentPath.startsWith('/signup') || currentPath.startsWith('/register') || currentPath.startsWith('/forgot-password') || currentPath.startsWith('/reset-password') || currentPath.startsWith('/forgotpassword');
			const isAuthEndpoint = error.config?.url?.includes('/auth/login') || error.config?.url?.includes('/auth/register') || error.config?.url?.includes('/auth/forgot-password') || error.config?.url?.includes('/auth/reset-password');
			// Allow farm produce add endpoints to surface errors without forcing logout
			const isFarmProduceEndpoint = error.config?.url?.includes('/auth/store/myfarmproduce');
			const hasToken = !!localStorage.getItem('authToken');
			// Only redirect if user has token but is not on auth pages and not making auth requests
			// This prevents redirects during login attempts
			if (hasToken && !isAuthPage && !isAuthEndpoint && !isFarmProduceEndpoint) {
				localStorage.removeItem('authToken');
				localStorage.removeItem('user');
				window.location.href = '/login';
			}
			// For auth endpoints (login/register), don't redirect - let the component handle the error
		}
		return Promise.reject(error);
	}
);

/**
 * Enhanced API wrapper with NETWORK-FIRST caching and offline support
 * 
 * Strategy:
 * 1. Always check internet connection first
 * 2. If online: 
 *    - GET requests: try network first, cache response, fallback to cache if network fails
 *    - POST/PUT/DELETE: process immediately (no caching), throw error if network fails
 * 3. If offline: 
 *    - GET requests: serve from cache
 *    - POST/PUT/DELETE: queue for later sync when online
 * This ensures critical operations are processed immediately when online.
 */

// Helper to optimistically patch farm management cache
const optimisticallyPatchFarmCache = async (method, url, data) => {
	try {
		// 1. Handle SINGLE ITEM VIEW: /manage/farm/:id/...
		const farmMatch = url.match(/\/manage\/farm\/(\d+)/);
		if (farmMatch) {
			const farmId = farmMatch[1];
			const getSingleUrl = `/manage/farm/${farmId}`;

			await cacheService.patchCachedResponse(getSingleUrl, (cachedData) => {
				if (url.includes('/stages') && method === 'POST') {
					// Add stage
					const newStage = {
						id: `temp_${Date.now()}`,
						stage_name: data.stage_name,
						start_date: data.start_date,
						end_date: data.end_date,
						stage_cost: data.expenses?.reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0) || 0,
						is_current: false,
						expenses: data.expenses || [],
						__offline: true
					};
					cachedData.growing_stages = [...(cachedData.growing_stages || []), newStage];
					cachedData.total_expenses = (parseFloat(cachedData.total_expenses) || 0) + newStage.stage_cost;
				} else if (url.includes('/activities') && method === 'POST') {
					// Add activity
					const newActivity = {
						id: `temp_${Date.now()}`,
						...data,
						__offline: true
					};
					cachedData.care_activities = [newActivity, ...(cachedData.care_activities || [])];
					cachedData.care_cost = (parseFloat(cachedData.care_cost) || 0) + (parseFloat(data.cost) || 0);
					cachedData.total_expenses = (parseFloat(cachedData.total_expenses) || 0) + (parseFloat(data.cost) || 0);
				} else if (url.includes('/activities') && method === 'DELETE') {
					// Delete activity
					const activityId = url.split('/').pop();
					const deleted = cachedData.care_activities?.find(a => a.id.toString() === activityId.toString());
					if (deleted) {
						cachedData.care_activities = cachedData.care_activities.filter(a => a.id.toString() !== activityId.toString());
						cachedData.care_cost = (parseFloat(cachedData.care_cost) || 0) - (parseFloat(deleted.cost) || 0);
						cachedData.total_expenses = (parseFloat(cachedData.total_expenses) || 0) - (parseFloat(deleted.cost) || 0);
					}
				} else if (url.includes('/stages') && method === 'PUT') {
					// Update stage (usually setting current)
					const stageId = url.split('/').pop();
					cachedData.growing_stages = cachedData.growing_stages?.map(s => {
						if (s.id.toString() === stageId.toString()) {
							return { ...s, ...data };
						}
						if (data.is_current) {
							return { ...s, is_current: false };
						}
						return s;
					});
				}
				return cachedData;
			});

			// 2. Handle LIST VIEW: /manage/farm
			const getListUrl = '/manage/farm';
			await cacheService.patchCachedResponse(getListUrl, (listData) => {
				if (!Array.isArray(listData)) return listData;

				return listData.map(item => {
					if (item.id.toString() === farmId.toString()) {
						if (url.includes('/stages') && method === 'POST') {
							const cost = data.expenses?.reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0) || 0;
							return {
								...item,
								total_expenses: (parseFloat(item.total_expenses) || 0) + cost
							};
						} else if (url.includes('/activities') && method === 'POST') {
							const cost = parseFloat(data.cost) || 0;
							return {
								...item,
								care_cost: (parseFloat(item.care_cost) || 0) + cost,
								total_expenses: (parseFloat(item.total_expenses) || 0) + cost
							};
						} else if (url.includes('/stages') && method === 'PUT' && data.is_current) {
							const stageId = url.split('/').pop();
							const stageName = item.growing_stages?.find(s => s.id.toString() === stageId.toString())?.stage_name;
							return {
								...item,
								current_stage: stageName || item.current_stage
							};
						}
					}
					return item;
				});
			});
		}

		// Handle /auth/store/myfarmproduce
		if (url.includes('/auth/store/myfarmproduce') && method === 'POST') {
			await cacheService.forceInvalidateCache(['/my_produce', '/manage/farm']);
		}
	} catch (error) {
		console.error('[API] Optimistic patch failed:', error);
	}
};

const cachedApiCall = async (method, url, data = null, config = {}) => {
	// NETWORK-FIRST STRATEGY: Always check internet connection first
	const isConnected = await networkUtils.isConnected();

	// Check if this is a mutation on a critical offline-first endpoint
	const isCriticalMutation = ['POST', 'PUT', 'DELETE'].includes(method) &&
		(url.includes('/manage/farm') || url.includes('/myfarmproduce') || url.includes('/auth/store/myfarmproduce'));

	if (isCriticalMutation) {
		// INSTANT-SUCCESS STRATEGY for critical mutations:
		// 1. Queue for sync
		const operationId = await cacheService.addToOfflineQueue({
			method,
			url,
			data,
			config
		});

		// 2. Optimistically patch the local cache so refreshes show the change
		await optimisticallyPatchFarmCache(method, url, data);

		// 3. Trigger background sync if online, but don't await it
		if (isConnected) {
			setTimeout(() => {
				cacheService.syncOfflineQueue().catch(err => console.error('Background sync failed:', err));
			}, 100);
		}

		// Return optimistic response
		return {
			status: true,
			message: isConnected ? 'Applying changes...' : 'Request queued for sync when online',
			data: (method === 'POST' || method === 'PUT') && data ? { ...data, id: `temp_${operationId}`, __offline: true } : null,
			__offline: true,
			__optimistic: true
		};
	}

	if (isConnected) {
		// We have internet - try network request first
		try {
			// Check if we have a dirty cache entry first
			if (method === 'GET') {
				const cacheKey = cacheService.generateCacheKey(url, method, data);
				const existing = await cacheService.apiCache.getItem(cacheKey);
				if (existing && existing.__dirty) {
					// console.log(`[API] Returning dirty cache for ${url} even though online`);
					if (existing.data.status !== undefined) {
						return { ...existing.data, __fromCache: true, __isDirty: true };
					}
					return { status: true, data: existing.data, __fromCache: true, __isDirty: true };
				}
			}

			const requestConfig = { ...config };
			const response = await api[method.toLowerCase()](url, data, requestConfig);

			// Cache successful GET responses
			if (method === 'GET' && response.data) {
				await cacheService.cacheResponse(url, response.data, method, data);
			}

			// For mutations (not already handled by the instant strategy), invalidate related cache 
			if (['POST', 'PUT', 'DELETE'].includes(method)) {
				await cacheService.invalidateRelatedCache(url);
			}

			return response.data;
		} catch (error) {
			// Network failed but we're online

			// For GET requests, try cache as fallback
			if (method === 'GET') {
				const cachedResponse = await cacheService.getCachedResponse(url, method, data);
				if (cachedResponse) {
					// Check if already wrapped
					if (cachedResponse.status !== undefined) {
						return {
							...cachedResponse,
							__fromCache: true
						};
					}
					return {
						status: true,
						data: cachedResponse,
						__fromCache: true
					};
				}
			}

			// For mutations, re-throw error immediately
			throw error;
		}
	} else {
		// We're offline - use cache-based handling

		// For GET requests, try to serve from cache
		if (method === 'GET') {
			const cachedResponse = await cacheService.getCachedResponse(url, method, data);
			if (cachedResponse) {
				if (cachedResponse.status !== undefined) {
					return {
						...cachedResponse,
						__fromCache: true,
						__offline: true
					};
				}
				return {
					status: true,
					data: cachedResponse,
					__fromCache: true,
					__offline: true
				};
			}

			// No cache available
			return {
				status: false,
				message: 'Offline - No cached data available',
				__offline: true
			};
		}

		// For non-GET requests (mutations not already handled), add to offline queue
		if (['POST', 'PUT', 'DELETE'].includes(method)) {
			const operationId = await cacheService.addToOfflineQueue({
				method,
				url,
				data,
				config
			});

			// For POST/PUT requests, return optimistic response
			if ((method === 'POST' || method === 'PUT') && data) {
				return {
					status: true,
					message: 'Request queued for sync when online',
					data: { ...data, id: `temp_${operationId}`, __offline: true },
					__offline: true
				};
			}

			// For other operations, return pending status
			return {
				status: true,
				message: 'Request queued for sync when online',
				__offline: true
			};
		}

		return {
			status: false,
			message: 'Offline - Operation not available',
			__offline: true
		};
	}
};


// Override the cache service's executeOfflineOperation method
cacheService.executeOfflineOperation = async (operation) => {
	try {
		const config = { ...operation.config };
		const response = await api[operation.method.toLowerCase()](
			operation.url,
			operation.data,
			config
		);
		return { success: true, data: response.data };
	} catch (error) {
		return {
			success: false,
			error: error.response?.data?.message || error.message
		};
	}
};

// Authentication API calls
export const authAPI = {
	// User Registration
	register: async (userData) => {
		const registrationData = {
			first_name: userData.first_name || userData.Firstname,
			last_name: userData.last_name || userData.Lastname,
			user_name: userData.user_name || userData.UserName,
			password: userData.password,
			role: userData.role || userData.userType,
			verification_type: userData.verification_type || (userData.email ? 'email' : 'phone'),
			...(userData.email && { email: userData.email }),
			...(userData.phone && { phone: userData.phone })
		};

		// Ensure verification_type is explicitly set
		if (!registrationData.verification_type) {
			registrationData.verification_type = userData.email ? 'email' : 'phone';
		}

		// Make direct API call for registration (bypass offline caching)
		const response = await api.post('/auth/register', registrationData);
		return response.data;
	},

	// Verify Email or Phone
	verifyEmailOrPhone: async (otp, email) => {
		// Convert OTP to number as expected by Laravel API
		// Make direct API call for verification (bypass offline caching)
		const response = await api.post('/auth/verify-email-or-phone', {
			otp: parseInt(otp.toString().trim(), 10),
			email: email
		});
		return response.data;
	},

	// Resend Verification Code
	resendVerification: async (userId) => {
		// Make direct API call for resend verification (bypass offline caching)
		const response = await api.post('/auth/resend-verification', {
			user_id: userId
		});
		return response.data;
	},

	// Update Farm Details (for Farmers)
	updateFarmDetails: async (farmData) => {
		// Make direct API call for farm details update (bypass offline caching)
		const response = await api.post('/auth/register/farm-details', farmData);
		return response.data;
	},

	// Update Expert Details (for Experts)
	updateExpertDetails: async (expertData) => {
		// expertData can be FormData (for registration with document) or regular object (for updates)
		// If it's FormData, it already has user_id, otherwise get from localStorage
		let formDataToSend;

		if (expertData instanceof FormData) {
			// For registration - FormData already contains user_id and document
			formDataToSend = expertData;
		} else {
			// For updates - get current user from localStorage
			const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
			formDataToSend = new FormData();
			formDataToSend.append('user_id', currentUser.id);

			// Append all fields from expertData
			Object.keys(expertData).forEach(key => {
				if (expertData[key] !== null && expertData[key] !== undefined) {
					// Handle arrays - send each item with array bracket notation (areas_of_expertise[]) so Laravel parses as array
					if (Array.isArray(expertData[key])) {
						expertData[key].forEach((item) => {
							formDataToSend.append(`${key}[]`, item);
						});
					} else if (typeof expertData[key] === 'object' && !(expertData[key] instanceof File)) {
						formDataToSend.append(key, JSON.stringify(expertData[key]));
					} else {
						formDataToSend.append(key, expertData[key]);
					}
				}
			});
		}

		// Make direct API call for expert details update (bypass offline caching)
		const response = await api.post('/auth/register/expert-details', formDataToSend, {
			headers: {
				'Content-Type': 'multipart/form-data',
			},
		});
		return response.data;
	},

	// Get Produce List
	getProduce: async () => {
		return await cachedApiCall('GET', '/auth/produce');
	},

	// Get My Produce
	getMyProduce: async () => {
		return await cachedApiCall('GET', '/my_produce');
	},

	// Get Single Produce View
	getProduceView: async (id) => {
		return await cachedApiCall('GET', `/view/my_produce/${id}`);
	},

	// Get Produce Care Information
	getProduceCare: async (id) => {
		return await cachedApiCall('GET', `/care/myproduce/${id}`);
	},

	// Delete Farm Produce
	deleteFarmProduce: async (id) => {
		return await cachedApiCall('DELETE', `/myfarmproduce/${id}`);
	},

	// Get Farm Management Data
	getFarmManagement: async () => {
		return await cachedApiCall('GET', '/manage/farm');
	},

	// Get Single Farm Management Data
	getSingleFarmManagement: async (id) => {
		return await cachedApiCall('GET', `/manage/farm/${id}`);
	},

	// Add Farm Activity
	addFarmActivity: async (id, activityData) => {
		return await cachedApiCall('POST', `/manage/farm/${id}/activities`, activityData);
	},

	// Delete Farm Activity
	deleteFarmActivity: async (activityId) => {
		return await cachedApiCall('DELETE', `/manage/farm/activities/${activityId}`);
	},

	// Add Farm Stage
	addFarmStage: async (id, stageData) => {
		return await cachedApiCall('POST', `/manage/farm/${id}/stages`, stageData);
	},

	// Update Farm Stage
	updateFarmStage: async (stageId, stageData) => {
		return await cachedApiCall('PUT', `/manage/farm/stages/${stageId}`, stageData);
	},

	// Store Farm Produce
	storeFarmProduce: async (produceIds, userId) => {
		return await cachedApiCall('POST', '/auth/store/myfarmproduce', {
			produce_id: produceIds,
			user_id: userId
		});
	},

	// Get Categories
	getCategories: async () => {
		return await cachedApiCall('GET', '/categories');
	},

	// Get All Products
	getProducts: async () => {
		return await cachedApiCall('GET', '/products');
	},

	// Get My Farm Produce
	getMyFarmProduce: async () => {
		return await cachedApiCall('GET', '/my_produce');
	},

	// Get Single Product
	getProduct: async (id) => {
		return await cachedApiCall('GET', `/products/${id}`);
	},

	// Create Product
	createProduct: async (productData) => {
		return await cachedApiCall('POST', '/products', productData);
	},

	// Update Product
	updateProduct: async (id, productData) => {
		return await cachedApiCall('PUT', `/products/${id}`, productData);
	},

	// Delete Product
	deleteProduct: async (id) => {
		return await cachedApiCall('DELETE', `/products/${id}`);
	},

	// Get Conversations
	getConversations: async () => {
		return await cachedApiCall('GET', '/conversations');
	},

	// Get Conversation Messages
	getConversationMessages: async (conversationId) => {
		return await cachedApiCall('GET', `/conversations/${conversationId}/messages`);
	},

	// Send Message
	sendMessage: async (messageData) => {
		return await cachedApiCall('POST', '/send-message', messageData);
	},

	// Get Expert Details
	getExpertDetails: async (expertId) => {
		return await cachedApiCall('GET', `/expert-details/${expertId}`);
	},

	// Get All Experts
	getExperts: async () => {
		return await cachedApiCall('GET', '/experts');
	},

	// Delete Conversation
	deleteConversation: async (conversationId) => {
		return await cachedApiCall('DELETE', `/delete-conversations/${conversationId}`);
	},

	// Get Farmer Orders
	getFarmerOrders: async () => {
		return await cachedApiCall('GET', '/farmer/orders');
	},

	// Get Farmer Order Details
	getFarmerOrderDetails: async (orderId) => {
		return await cachedApiCall('GET', `/farmer/orders/${orderId}`);
	},

	// Confirm Seller Transaction
	confirmSellerTransaction: async (orderId) => {
		return await cachedApiCall('PUT', `/orders/${orderId}/confirm-seller`);
	},

	// Get Farmer Withdrawals
	getFarmerWithdrawals: async () => {
		return await cachedApiCall('GET', '/farmer/withdrawals');
	},

	// Create Withdrawal Request
	createWithdrawal: async (withdrawalData) => {
		return await cachedApiCall('POST', '/farmer/withdraw', withdrawalData);
	},

	// Get Farmer Wallet
	getFarmerWallet: async () => {
		return await cachedApiCall('GET', '/farmer/wallet');
	},

	// User Login
	login: async (credentials) => {
		// Add login_type based on whether email or phone is provided
		const loginData = {
			...credentials,
			login_type: credentials.email ? 'email' : 'phone'
		};
		// Make direct API call for login (bypass offline caching)
		const response = await api.post('/auth/login', loginData);
		return response.data;
	},

	// Password Reset Request
	forgotPassword: async (login) => {
		const response = await api.post('/auth/forgot-password', { login });
		return response.data;
	},

	// Reset Password with Token
	resetPassword: async (login, token, password, password_confirmation) => {
		const response = await api.post('/auth/reset-password', {
			login,
			token,
			password,
			password_confirmation
		});
		return response.data;
	},

	// Upload Avatar
	uploadAvatar: async (file) => {
		const formData = new FormData();
		formData.append('avatar', file);
		const response = await api.post('/profile/avatar', formData, {
			headers: {
				'Content-Type': 'multipart/form-data',
			},
		});
		return response.data;
	},

	// Verify Email
	verifyEmail: async (token) => {
		const response = await api.post('/auth/verify-email', { token });
		return response.data;
	},

	// Get User Profile
	getProfile: async () => {
		const response = await api.get('/profile');
		return response.data;
	},

	// Update User Profile
	updateProfile: async (profileData) => {
		const response = await api.put('/auth/profile', profileData);
		return response.data;
	},

	// Change Password
	changePassword: async (currentPassword, newPassword) => {
		const response = await api.put('/auth/change-password', {
			currentPassword,
			newPassword,
		});
		return response.data;
	},

	// Logout
	logout: async () => {
		const response = await api.post('/auth/logout');
		return response.data;
	},

	// Get Appointments
	getAppointments: async () => {
		const response = await api.get('/appointments');
		return response.data;
	},

	// Get Appointment Details
	getAppointmentDetails: async (appointmentId) => {
		const response = await api.get(`/appointments/${appointmentId}`);
		return response.data;
	},

	// Cancel Appointment
	cancelAppointment: async (appointmentId) => {
		const response = await api.put(`/appointments/${appointmentId}/cancel`);
		return response.data;
	},

	// Get User Profile (Updated endpoint)
	getUserProfile: async () => {
		const response = await api.get('/profile');
		return response.data;
	},

	// Update User Profile Field
	updateUserProfile: async (field, value) => {
		const response = await api.put('/profile/update', { field, value });
		return response.data;
	},

	// Get Notification Preferences
	getNotificationPreferences: async () => {
		const response = await api.get('/notification-preferences');
		return response.data;
	},

	// Update Notification Preferences
	updateNotificationPreferences: async (preferences) => {
		const response = await api.put('/notification-preferences', preferences);
		return response.data;
	},

	// Change Password (Updated endpoint)
	changeUserPassword: async (currentPassword, newPassword, newPasswordConfirmation) => {
		const response = await api.put('/change-password', {
			current_password: currentPassword,
			new_password: newPassword,
			new_password_confirmation: newPasswordConfirmation
		});
		return response.data;
	},

	// Get Privacy Settings
	getPrivacySettings: async () => {
		const response = await api.get('/settings');
		return response.data;
	},

	// Update Privacy Settings
	updatePrivacySettings: async (settings) => {
		const response = await api.put('/settings', settings);
		return response.data;
	},

	// Calendar/Event Management
	// Get user's calendar events
	getCalendarEvents: async () => {
		try {
			const response = await api.get('/calendar/events');
			return response.data;
		} catch (error) {
			console.error('Error fetching calendar events:', error);
			// Return empty object for offline/error scenarios
			return { success: true, data: {} };
		}
	},

	// Add or update calendar event
	saveCalendarEvent: async (date, eventType) => {
		try {
			const response = await api.post('/calendar/events', {
				date,
				eventType
			});
			return response.data;
		} catch (error) {
			console.error('Error saving calendar event:', error);
			// Return success for offline scenarios
			return { success: true, data: { date, eventType } };
		}
	},

	// Delete calendar event
	deleteCalendarEvent: async (date) => {
		try {
			const response = await api.delete(`/calendar/events/${date}`);
			return response.data;
		} catch (error) {
			console.error('Error deleting calendar event:', error);
			// Return success for offline scenarios
			return { success: true };
		}
	},

	// Bulk update calendar events
	updateCalendarEvents: async (events) => {
		try {
			const response = await api.put('/calendar/events', { events });
			return response.data;
		} catch (error) {
			console.error('Error updating calendar events:', error);
			// Return success for offline scenarios
			return { success: true, data: events };
		}
	},

	// Expert Dashboard APIs
	// Update Expert Profile (authenticated update)
	updateExpertProfile: async (expertData) => {
		const response = await api.put('/expert/update-details', expertData);
		return response.data;
	},

	// Get Expert Overview
	getExpertOverview: async () => {
		return await cachedApiCall('GET', '/expert/overview');
	},

	// Get Expert Upcoming Appointments
	getExpertUpcomingAppointments: async () => {
		return await cachedApiCall('GET', '/expert/upcoming-appointments');
	},

	// Get Expert Withdrawals
	getExpertWithdrawals: async () => {
		return await cachedApiCall('GET', '/expert/withdrawals');
	},

	// Get Expert Wallet Balance
	getExpertWallet: async () => {
		return await cachedApiCall('GET', '/expert/wallet');
	},

	// Create Expert Withdrawal Request
	createExpertWithdrawal: async (withdrawalData) => {
		return await cachedApiCall('POST', '/expert/withdraw', withdrawalData);
	},

	// Get Expert Transactions
	getExpertTransactions: async () => {
		return await cachedApiCall('GET', '/expert/transactions');
	},

	// Get Expert Appointments
	getExpertAppointments: async () => {
		return await cachedApiCall('GET', '/expert/appointments');
	},

	// Get Expert Appointment Details
	getExpertAppointmentDetails: async (appointmentId) => {
		return await cachedApiCall('GET', `/expert/appointments/${appointmentId}`);
	},


	// Retailer Dashboard APIs
	// Get Retailer Orders
	getRetailerOrders: async () => {
		return await cachedApiCall('GET', '/retailer/orders');
	},

	// Get Retailer Products
	getRetailerProducts: async () => {
		return await cachedApiCall('GET', '/retailer/products');
	},

	// Get Retailer Cart
	getRetailerCart: async () => {
		return await cachedApiCall('GET', '/retailer/cart');
	},

	// Add to Cart
	addToCart: async (productData) => {
		return await cachedApiCall('POST', '/retailer/cart/add', productData);
	},

	// Update Cart Item
	updateCartItem: async (cartId, quantity) => {
		return await cachedApiCall('PUT', `/retailer/cart/${cartId}`, { quantity });
	},

	// Delete Cart Item
	deleteCartItem: async (cartId) => {
		return await cachedApiCall('DELETE', `/retailer/cart/${cartId}`);
	},

	// Checkout
	checkout: async (checkoutData) => {
		return await cachedApiCall('POST', '/retailer/checkout', checkoutData);
	},

	// Verify Payment
	verifyPayment: async (reference) => {
		return await cachedApiCall('POST', '/retailer/verify-payment', { reference });
	},

	// Confirm Buyer Transaction (for retailers)
	confirmBuyerTransaction: async (orderId) => {
		return await cachedApiCall('PUT', `/orders/${orderId}/confirm-buyer`);
	},

	// AI Assistant APIs
	sendAiMessage: async (formData) => {
		// formdata: message, language, image (optional), session_id (optional)
		const response = await api.post('/ai/send-message', formData, {
			headers: {
				'Content-Type': 'multipart/form-data',
			},
		});
		return response.data;
	},

	getAiHistory: async (sessionId = null) => {
		const url = sessionId ? `/ai/history?session_id=${sessionId}` : '/ai/history';
		const response = await api.get(url);
		return response.data;
	},

	getAiMessages: async () => {
		const response = await api.get('/ai/messages');
		return response.data;
	},

	deleteAiSession: async (sessionId) => {
		const response = await api.delete('/ai/session', {
			data: { session_id: sessionId }
		});
		return response.data;
	},
};

// Export the main api instance for other services
export { api };
export default authAPI;
